/**
 * Calendar export (issue #373): the pure RFC 5545 ICS builder behind the
 * paid-gated export queries. An item's date-only dueAt (UTC-midnight
 * epoch) becomes an all-day VEVENT; the structured recurrence rule
 * translates to RRULE (FREQ/INTERVAL/BYDAY — month-end clamping is left
 * to the importing calendar, which every target applies natively); a
 * floating reminderAt becomes a VALARM concretized against the
 * caller-supplied IANA timezone at export time (one-way: the floating
 * convention governs in-app reminders, never the file). Notes render to
 * a capped plain-text DESCRIPTION. UIDs are the caller's (stable item
 * ids), so re-importing an export updates rather than duplicates.
 *
 * Text values are escaped per RFC 5545 §3.3.11 and every content line is
 * folded at 75 OCTETS (bytes, not characters — counting code units would
 * split multibyte runes mid-sequence) with the single-space continuation.
 * Lines join with CRLF and the output ends with a CRLF, as the grammar
 * requires.
 */

import type { RecurrenceRule } from "./recurrence";
import {
  type FloatingDateTime,
  parseFloatingDateTime,
  resolveFloatingToInstant,
} from "./floatingTime";
// Circular by design: index re-exports this module (from the bottom of
// its body, after its consts initialize) and this module needs
// dueMsToDate — a hoisted function declaration, so the back-reference
// resolves under every ESM evaluation order. Proven live on all three
// consumers (vitest, the Convex bundle, the vite-served web app).
import { dueMsToDate } from "./index";

/**
 * The server's gate (exports:listCalendar / exports:itemCalendar) and
 * the client upsell surfaces say the same thing in the same words
 * (the PAID_RECURRENCE_MESSAGE pattern).
 */
export const PAID_EXPORT_MESSAGE =
  "Calendar export is a paid feature — upgrade to export to Apple or Google Calendar.";

/** A list with zero dated items refuses rather than serving an empty file. */
export const EXPORT_EMPTY_MESSAGE =
  "Nothing to export yet — no items in this list have a due date.";

/** The item flavor refuses an item that has no due date to anchor an event. */
export const EXPORT_ITEM_NEEDS_DUE_MESSAGE =
  "This item has no due date to export.";

/** What a tap on an Export to calendar entry does for this viewer. */
export type ExportTapOutcome = "upsell" | "export";

/**
 * Which export the viewer asked for — one flavor per entry, the two
 * hooks' (#374 mobile, #375 web) shared request vocabulary.
 */
export type ExportRequest =
  | { kind: "list"; listId: string }
  | { kind: "item"; itemId: string };

/**
 * A free tap shows the upsell and never reaches the seam; a paid tap
 * exports. An unresolved entitlement reads free — the
 * deleteConfirmationCopy fail-safe (ADR-0009): the entitlements query
 * is briefly undefined at cold start, and the seam refuses an unpaid
 * caller anyway, so the worst case is a paid member seeing the upsell
 * for a moment, never a gated action firing on a guess. One decision
 * for every surface (#374 mobile, #375 web) next to the copy it
 * guards, so the branch can never drift between them.
 */
export function resolveExportTap(viewerIsPaid: boolean): ExportTapOutcome {
  return viewerIsPaid ? "export" : "upsell";
}

/**
 * The plain-text DESCRIPTION cap: notes are rich text and can grow
 * arbitrarily, while a calendar event's description is a glance field.
 */
export const EXPORT_DESCRIPTION_MAX_CHARS = 800;

const MAX_LINE_OCTETS = 75;
const MAX_FILENAME_CHARS = 60;

/** One event in the export. `uid` is caller-supplied and must be stable. */
export type IcsCalendarEvent = {
  uid: string;
  /** All-day start date, `YYYY-MM-DD` (derived from the item's dueAt). */
  date: string;
  summary: string;
  /** Plain text, already capped — rendered only when non-empty. */
  description?: string;
  /** The structured rule from the item, translated to RRULE at build. */
  recurrence?: RecurrenceRule;
  /** The floating wall-clock reminder, concretized against `timeZone`. */
  reminderFloating?: string;
};

/** The plain fields of an item row the export mapping needs — no Convex types. */
export type CalendarExportItem = {
  id: string;
  title: string;
  dueAt: number;
  recurrence?: RecurrenceRule;
  reminderAt?: string;
  notes?: unknown;
};

export type IcsCalendarInput = {
  /** Calendar name — exported as X-WR-CALNAME (the list name). */
  name: string;
  events: IcsCalendarEvent[];
  /** IANA zone the caller exports from; unknown zones fall back to UTC. */
  timeZone: string;
  /** The DTSTAMP instant (ms) — passed in so the builder stays pure. */
  now: number;
};

/**
 * One item row -> one all-day event. The UID is `<item id>@todotogether`
 * — stable across exports, so re-importing an updated export updates the
 * events instead of duplicating them.
 */
export function calendarEventFromItem(item: CalendarExportItem): IcsCalendarEvent {
  const text = notesToPlainText(item.notes);
  return {
    uid: `${item.id}@todotogether`,
    date: dueMsToDate(item.dueAt),
    summary: item.title,
    description: text === "" ? undefined : capText(text),
    recurrence: item.recurrence,
    reminderFloating: item.reminderAt,
  };
}

/**
 * Build the full VCALENDAR text. Empty event lists are valid here — the
 * zero-dated refusal is the export query's job, not the builder's.
 */
export function buildIcsCalendar(input: IcsCalendarInput): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ToDoTogether//Calendar Export//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapeText(input.name)}`,
  ];
  for (const event of input.events) {
    lines.push(
      ...buildEvent(event, input.timeZone, input.now),
    );
  }
  lines.push("END:VCALENDAR");
  return lines.flatMap(foldLine).join("\r\n") + "\r\n";
}

function buildEvent(
  event: IcsCalendarEvent,
  timeZone: string,
  now: number,
): string[] {
  const lines = [
    "BEGIN:VEVENT",
    `UID:${event.uid}`,
    `DTSTAMP:${formatUtcBasic(now)}`,
    `DTSTART;VALUE=DATE:${basicDate(event.date)}`,
    `DTEND;VALUE=DATE:${basicDate(nextDay(event.date))}`,
  ];
  if (event.recurrence !== undefined) {
    lines.push(rrule(event.recurrence));
  }
  lines.push(`SUMMARY:${escapeText(event.summary)}`);
  if (event.description !== undefined && event.description !== "") {
    lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  }
  const alarm = valarm(event, timeZone);
  if (alarm !== null) lines.push(...alarm);
  lines.push("END:VEVENT");
  return lines;
}

const RRULE_FREQ = {
  day: "DAILY",
  week: "WEEKLY",
  month: "MONTHLY",
  year: "YEARLY",
} as const;

/** ICS BYDAY codes, index-aligned with RecurrenceWeekday (0 = Sunday). */
const ICS_WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

function rrule(rule: RecurrenceRule): string {
  const parts = [`FREQ=${RRULE_FREQ[rule.unit]}`];
  if (rule.interval !== 1) parts.push(`INTERVAL=${rule.interval}`);
  if (rule.unit === "week" && rule.weekday !== undefined) {
    parts.push(`BYDAY=${ICS_WEEKDAYS[rule.weekday]}`);
  }
  return `RRULE:${parts.join(";")}`;
}

/**
 * The reminder as an absolute-time VALARM, or null when the floating
 * string is invalid. An unknown timezone resolves against UTC rather
 * than dropping the reminder (the floatingTime fallback convention).
 */
function valarm(
  event: IcsCalendarEvent,
  timeZone: string,
): string[] | null {
  if (event.reminderFloating === undefined) return null;
  const instant =
    resolveFloatingToInstant(event.reminderFloating, timeZone) ??
    resolveFloatingToInstant(event.reminderFloating, "UTC");
  if (instant === null) return null;
  return [
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escapeText(event.summary)}`,
    `TRIGGER;VALUE=DATE-TIME:${formatUtcBasic(instant)}`,
    "END:VALARM",
  ];
}

/** RFC 5545 §3.3.11 TEXT escaping: backslash first, then ; , and newline. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

/** `YYYY-MM-DD` -> `YYYYMMDD` (the ICS basic date format). */
function basicDate(date: string): string {
  return date.replaceAll("-", "");
}

/** The day after a `YYYY-MM-DD`, as UTC calendar arithmetic. */
function nextDay(date: string): string {
  const parts = parseFloatingDateTime(`${date}T00:00`);
  // The date strings this module produces are always calendar-real.
  const p: FloatingDateTime = parts ?? { year: 1970, month: 1, day: 1, hour: 0, minute: 0 };
  return new Date(Date.UTC(p.year, p.month - 1, p.day + 1))
    .toISOString()
    .slice(0, 10);
}

/** Epoch ms -> `YYYYMMDDTHHMMSSZ` (ICS basic UTC date-time). */
function formatUtcBasic(ms: number): string {
  return new Date(ms)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

/**
 * Fold one content line at 75 octets (RFC 5545 §3.1): continuation lines
 * start with a single space, which counts toward the limit. Byte counts
 * come from TextEncoder, and iteration is by code point, so a multibyte
 * rune is never split across lines.
 */
function foldLine(line: string): string[] {
  const encoder = new TextEncoder();
  const lines: string[] = [];
  let current = "";
  let bytes = 0;
  for (const ch of line) {
    const width = encoder.encode(ch).length;
    const limit = lines.length === 0 ? MAX_LINE_OCTETS : MAX_LINE_OCTETS - 1;
    if (bytes + width > limit) {
      lines.push(current);
      current = " " + ch;
      bytes = 1 + width;
    } else {
      current += ch;
      bytes += width;
    }
  }
  lines.push(current);
  return lines;
}

/**
 * BlockNote blocks -> plain text: the text runs of each block joined
 * with spaces, blocks joined with newlines, `children` walked. Embed
 * blocks carry no text content and contribute nothing naturally, so
 * there is no embed special case to drift. Defensive throughout — this
 * reads stored JSON — with an explicit stack and a depth bound (the
 * stored docs are already capped far below it).
 */
export function notesToPlainText(blocks: unknown): string {
  if (!Array.isArray(blocks)) return "";
  const lines: string[] = [];
  // Pushed in reverse so the stack pops in document order (the top level
  // included), and children re-push the same way — the explicit-stack
  // walk must not reverse what it flattens.
  const stack: Array<{ node: unknown; depth: number }> = [];
  for (let i = blocks.length - 1; i >= 0; i--) {
    stack.push({ node: blocks[i], depth: 1 });
  }
  while (stack.length > 0) {
    const { node, depth } = stack.pop()!;
    if (typeof node !== "object" || node === null) continue;
    const block = node as { content?: unknown; children?: unknown };
    const text = blockText(block.content);
    if (text !== "") lines.push(text);
    if (Array.isArray(block.children) && depth < 16) {
      for (let i = block.children.length - 1; i >= 0; i--) {
        stack.push({ node: block.children[i], depth: depth + 1 });
      }
    }
  }
  return lines.join("\n");
}

/** The block's text runs concatenated (inline segments, no separator). */
function blockText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  let text = "";
  for (const run of content) {
    if (typeof run === "object" && run !== null) {
      const t = (run as { text?: unknown }).text;
      if (typeof t === "string") text += t;
    }
  }
  return text;
}

/** Cap to EXPORT_DESCRIPTION_MAX_CHARS with an ellipsis marker. */
function capText(text: string): string {
  if (text.length <= EXPORT_DESCRIPTION_MAX_CHARS) return text;
  return text.slice(0, EXPORT_DESCRIPTION_MAX_CHARS) + "…";
}

/**
 * A safe `.ics` filename for a list or item name: the Windows-forbidden
 * characters and control characters become spaces (the file travels
 * through share sheets and downloads on every platform), whitespace
 * runs collapse, and an empty result falls back to "calendar".
 */
export function icsFilenameFor(name: string): string {
  const cleaned = name
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Truncate by code point: a UTF-16 slice could split a surrogate pair
  // (emoji in list names) and strand a lone surrogate in the filename.
  const base =
    cleaned === "" ? "calendar" : Array.from(cleaned).slice(0, MAX_FILENAME_CHARS).join("");
  return `${base}.ics`;
}
